/**
 * 修炼系统
 * 处理主动修炼、突破、被动修炼
 */

import type { PlayerState } from './Player';
import { getExpMultiplier, recalcStats, getPlayerIdentity } from './Player';
import { findRealmLevel, getNextRealmLevel } from '../data/realms';
import { REALMS } from '../data/realms';

export interface CultivationResult {
  success: boolean;
  message: string;
  expGained?: number;
  mpCost?: number;
  breakthrough?: boolean;
  newRealmKey?: string;
}

/**
 * 主动修炼：消耗MP获得修炼经验
 */
export function cultivate(player: PlayerState): CultivationResult {
  const realmLevel = findRealmLevel(player.realmKey);
  if (!realmLevel) return { success: false, message: '无法获取当前境界信息' };

  const mpCost = 10;
  if (player.mp < mpCost) {
    return { success: false, message: '灵力不足，无法修炼！需要至少 10 点灵力' };
  }

  // 基础经验 + 随机波动（受灵根+身份修炼速度加成）
  const expMult = getExpMultiplier(player);
  const baseExp = Math.floor((15 + Math.floor(player.attack * 0.3)) * expMult);
  const variance = Math.floor(Math.random() * 10) - 5; // -5 ~ +4
  const expGained = Math.max(1, baseExp + variance);

  player.mp -= mpCost;
  player.cultivationExp += expGained;

  const progress = (player.cultivationExp / realmLevel.expRequired) * 100;

  return {
    success: true,
    message: `潜心修炼，获得 ${expGained} 点修炼经验（消耗 ${mpCost} 灵力）`,
    expGained,
    mpCost,
  };
}

/**
 * 判断是否为大境界突破（第9层→下一境界第1层）
 */
function isRealmBreakthrough(currentKey: string): boolean {
  return currentKey.endsWith('_9');
}

/** 查找同一境界的某层key */
function getLayerKey(realmKey: string, stage: number): string {
  const parts = realmKey.split('_');
  parts[parts.length - 1] = String(stage);
  return parts.join('_');
}

/**
 * 尝试突破到下一层
 */
export function breakthrough(player: PlayerState): CultivationResult {
  const realmLevel = findRealmLevel(player.realmKey);
  if (!realmLevel) return { success: false, message: '无法获取当前境界信息' };

  if (player.cultivationExp < realmLevel.expRequired) {
    return { success: false, message: `修炼经验不足！当前 ${player.cultivationExp}/${realmLevel.expRequired}` };
  }

  const nextLevel = getNextRealmLevel(player.realmKey);
  if (!nextLevel) {
    return { success: false, message: '你已到达修仙之路的尽头——渡劫期第九层！飞升在即！' };
  }

  const isMajor = isRealmBreakthrough(player.realmKey);
  const rootBonus = player.spiritualRoot?.breakthroughBonus ?? 0;
  const identity = getPlayerIdentity(player);
  const identityBonus = identity?.permBreakthroughBonus ?? 0;

  // 大境界突破：基础成功率打四折
  let effectiveRate = realmLevel.breakthroughRate + rootBonus + identityBonus;
  if (isMajor) {
    effectiveRate = effectiveRate * 0.4;
  }

  // 突破丹
  let usedPill = false;
  const pillIdx = player.inventory.findIndex((i) => i.id === 'breakthrough_pill');
  if (pillIdx !== -1) {
    player.inventory.splice(pillIdx, 1);
    usedPill = true;
    effectiveRate += 0.30;
  }

  effectiveRate = Math.min(0.95, Math.max(0.03, effectiveRate));
  const roll = Math.random();

  if (roll <= effectiveRate) {
    // 成功
    const oldName = `${realmLevel.realmName}第${realmLevel.stage}层`;
    player.realmKey = nextLevel.key;
    player.cultivationExp = 0;
    recalcStats(player);
    player.hp = player.maxHp;
    player.mp = player.maxMp;

    const pillMsg = usedPill ? '（筑基丹辅助）' : '';
    const typeMsg = isMajor ? `🌟 大境界突破成功！` : `🎉 突破成功！`;
    return {
      success: true,
      message: `${typeMsg}从 ${oldName} 晋升至 ${nextLevel.realmName}第${nextLevel.stage}层！${pillMsg}`,
      breakthrough: true,
      newRealmKey: nextLevel.key,
    };
  } else {
    // 失败
    let msg = '';
    if (isMajor) {
      // 大境界失败：跌落1~2层
      const fallback = Math.max(1, realmLevel.stage - (Math.random() < 0.5 ? 1 : 2));
      player.realmKey = getLayerKey(player.realmKey, fallback);
      player.cultivationExp = 0;

      const realmLevel2 = findRealmLevel(player.realmKey);
      const damage = Math.floor(player.maxHp * 0.5);
      player.hp = Math.max(1, player.hp - damage);

      msg = `💀 大境界突破失败！境界跌落至${realmLevel2?.realmName ?? '?'}第${fallback}层，受到 ${damage} 点反噬伤害！`;
      if (usedPill) msg += ' 筑基丹已消耗。';
      recalcStats(player);
    } else {
      // 小境界失败：损失经验
      const expLoss = Math.floor(realmLevel.expRequired * 0.3);
      player.cultivationExp = Math.max(0, player.cultivationExp - expLoss);
      msg = `❌ 突破失败！损失 ${expLoss} 点修炼经验。`;
      if (usedPill) msg += ' 筑基丹已消耗。';

      if (Math.random() < 0.3) {
        const damage = Math.floor(player.maxHp * 0.2);
        player.hp = Math.max(1, player.hp - damage);
        msg += ` 受到 ${damage} 点反噬伤害。`;
      }
    }

    return { success: false, message: msg };
  }
}

/**
 * 计算被动修炼收益（挂机/离线）
 */
export function passiveCultivate(player: PlayerState, elapsedSeconds: number): CultivationResult {
  const realmLevel = findRealmLevel(player.realmKey);
  if (!realmLevel) return { success: false, message: '' };

  // 限制最大离线收益为8小时（受灵根+身份修炼速度加成）
  const cappedSeconds = Math.min(elapsedSeconds, 8 * 3600);
  const expMult = getExpMultiplier(player);
  const expGained = Math.floor(cappedSeconds * realmLevel.passiveExpPerSec * expMult);

  if (expGained <= 0) {
    return { success: true, message: '', expGained: 0 };
  }

  player.cultivationExp += expGained;
  // 离线时灵力也会缓慢恢复
  const mpRecovered = Math.floor(cappedSeconds * 0.5);
  player.mp = Math.min(player.maxMp, player.mp + mpRecovered);

  return {
    success: true,
    message: `⏰ 离线修炼中：你离开了 ${formatDuration(cappedSeconds)}，获得 ${expGained} 点修炼经验`,
    expGained,
  };
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}秒`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}分钟`;
  return `${(seconds / 3600).toFixed(1)}小时`;
}
